// src/lib/kinema/core/tools/KeyMap.ts
import type { Editor } from '../Editor';
import type { ToolManager } from './ToolManager';
import { SELECT_LAYER, REMOVE_LAYER, UPDATE_PROPERTY, ADD_LAYER } from '../commands/commands';
import { PenTool } from './PenTool';
import { SelectTool } from './SelectTool';

export type KeyAction = (editor: Editor, toolManager?: ToolManager) => void;
export type KeyMap = Record<string, string>;

export const defaultKeymap: KeyMap = {
  'Delete': 'deleteSelected',
  'Backspace': 'deleteSelected',
  'ArrowUp': 'nudgeUp',
  'ArrowDown': 'nudgeDown',
  'ArrowLeft': 'nudgeLeft',
  'ArrowRight': 'nudgeRight',
  'Shift+ArrowUp': 'nudgeUpLarge',
  'Shift+ArrowDown': 'nudgeDownLarge',
  'Shift+ArrowLeft': 'nudgeLeftLarge',
  'Shift+ArrowRight': 'nudgeRightLarge',
  'Escape': 'deselectAll',
  'Meta+z': 'undo',
  'Meta+Shift+z': 'redo',
  'Control+z': 'undo',
  'Control+Shift+z': 'redo',
  'Meta+a': 'selectAll',
  'Control+a': 'selectAll',
  'Meta+c': 'copy',
  'Control+c': 'copy',
  'Meta+v': 'paste',
  'Control+v': 'paste',
  'Meta+d': 'duplicate',
  'Control+d': 'duplicate',
  ' ': 'togglePlay',
  'v': 'toolSelect',
  'h': 'toolPan',
  'p': 'toolPen',
};

// Module-level clipboard for copy/paste
let clipboard: any[] = [];

function cloneLayerForPaste(layer: any, offset: number): any {
  // Deep clone, strip id so ADD_LAYER generates a new one
  const { id, ...rest } = JSON.parse(JSON.stringify(layer));
  // Offset position so paste isn't exactly on top
  rest.x = (rest.x ?? 0) + offset;
  rest.y = (rest.y ?? 0) + offset;
  // Generate new effect IDs
  if (rest.effects) {
    const newEffects: Record<string, any> = {};
    for (const [prop, effect] of Object.entries(rest.effects) as [string, any][]) {
      newEffects[prop] = { ...effect, id: `eff-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
    }
    rest.effects = newEffects;
  }
  return rest;
}

export const builtInActions: Record<string, KeyAction> = {
  deleteSelected: (editor, toolManager) => {
    // If in path edit mode, delete the selected point instead of the layer
    const activeTool = toolManager?.getActiveTool();
    if (activeTool instanceof SelectTool && activeTool.isEditingPath()) {
      activeTool.deleteSelectedPoint();
      return;
    }
    const ids = Array.from(editor.getState().selectedIds);
    if (ids.length === 0) return;
    editor.batch(() => {
      for (const id of ids) editor.dispatchCommand(REMOVE_LAYER, { layerId: id });
    });
  },
  nudgeUp: (editor) => nudge(editor, 'y', -1),
  nudgeDown: (editor) => nudge(editor, 'y', 1),
  nudgeLeft: (editor) => nudge(editor, 'x', -1),
  nudgeRight: (editor) => nudge(editor, 'x', 1),
  nudgeUpLarge: (editor) => nudge(editor, 'y', -10),
  nudgeDownLarge: (editor) => nudge(editor, 'y', 10),
  nudgeLeftLarge: (editor) => nudge(editor, 'x', -10),
  nudgeRightLarge: (editor) => nudge(editor, 'x', 10),
  deselectAll: (editor, toolManager) => {
    // If pen tool is active and drawing, cancel the path instead of deselecting
    const activeTool = toolManager?.getActiveTool();
    if (activeTool instanceof PenTool && activeTool.getMode() !== 'idle') {
      activeTool.cancel();
      return;
    }
    editor.dispatchCommand(SELECT_LAYER, { layerId: null });
  },
  undo: (editor) => editor.undo(),
  redo: (editor) => editor.redo(),
  selectAll: (editor) => {
    const ids = Object.keys(editor.getState().layers);
    editor.batch(() => {
      for (const id of ids) editor.dispatchCommand(SELECT_LAYER, { layerId: id, addToSelection: true });
    });
  },
  copy: (editor) => {
    const state = editor.getState();
    const ids = Array.from(state.selectedIds);
    if (ids.length === 0) return;
    clipboard = ids.map((id) => state.layers[id]).filter(Boolean);
  },
  paste: (editor) => {
    if (clipboard.length === 0) return;
    editor.batch(() => {
      for (const layer of clipboard) {
        editor.dispatchCommand(ADD_LAYER, cloneLayerForPaste(layer, 20));
      }
    });
  },
  duplicate: (editor) => {
    const state = editor.getState();
    const ids = Array.from(state.selectedIds);
    if (ids.length === 0) return;
    const layers = ids.map((id) => state.layers[id]).filter(Boolean);
    editor.batch(() => {
      for (const layer of layers) {
        editor.dispatchCommand(ADD_LAYER, cloneLayerForPaste(layer, 20));
      }
    });
  },
  toolSelect: (_editor, toolManager) => toolManager?.setActiveTool('select'),
  toolPan: (_editor, toolManager) => toolManager?.setActiveTool('pan'),
  toolPen: (_editor, toolManager) => toolManager?.setActiveTool('pen'),
  togglePlay: (editor) => {
    if (editor.getState().isPlaying) {
      editor.pause();
    } else {
      editor.play();
    }
  },
};

function nudge(editor: Editor, property: 'x' | 'y', delta: number): void {
  const ids = Array.from(editor.getState().selectedIds);
  if (ids.length === 0) return;
  editor.batch(() => {
    for (const id of ids) {
      const current = editor.getState().layers[id]?.[property] ?? 0;
      editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property, value: current + delta });
    }
  });
}

export function resolveKeyCombo(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.metaKey) parts.push('Meta');
  if (event.ctrlKey) parts.push('Control');
  if (event.shiftKey) parts.push('Shift');
  parts.push(event.key);
  return parts.join('+');
}

export function useKeyboardShortcuts(
  editor: Editor,
  keymap: Partial<KeyMap> = {},
  actions: Record<string, KeyAction> = {},
): (event: KeyboardEvent) => void {
  const mergedKeymap: KeyMap = Object.assign({}, defaultKeymap, keymap) as KeyMap;
  const mergedActions: Record<string, KeyAction> = { ...builtInActions, ...actions };

  return (event: KeyboardEvent) => {
    const combo = resolveKeyCombo(event);
    const actionName = mergedKeymap[combo];
    if (!actionName) return;
    const action = mergedActions[actionName];
    if (!action) return;
    event.preventDefault();
    action(editor);
  };
}
